import { useState } from 'react';
import type { Company } from '../../../../../shared/types';
import { BaseModal } from '../../common/BaseModal';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { ERROR_MESSAGE_STYLE, INPUT_STYLE } from '../../../styles/modalStyles';

interface DeleteCompanyModalProps {
  company: Company;
  onClose: () => void;
  onDelete: (companyId: string) => Promise<void>;
}

export function DeleteCompanyModal({ company, onClose, onDelete }: DeleteCompanyModalProps) {
  const [step, setStep] = useState<'first' | 'second'>('first');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationText, setConfirmationText] = useState('');

  const handleFirstConfirmation = () => {
    setStep('second');
  };

  const handleFinalDelete = async () => {
    if (confirmationText !== company.name) {
      setError('Company name does not match. Please type the exact company name.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await onDelete(company.id);
      onClose();
    } catch (err) {
      console.error('Error deleting company:', err);
      setError('Failed to permanently delete company. Please try again.');
      setLoading(false);
    }
  };

  if (step === 'first') {
    return (
      <BaseModal
        isOpen={true}
        onClose={onClose}
        title=""
        size="medium"
        closeOnEscape={true}
        closeOnBackdropClick={true}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{ fontSize: '32px' }}>⚠️</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626', margin: 0 }}>
            Permanent Deletion Warning
          </h2>
        </div>

        <div style={{ marginBottom: '24px', color: '#6b7280', fontSize: '15px', lineHeight: '1.6' }}>
          <p style={{ marginBottom: '12px', color: '#111827', fontWeight: '600' }}>
            You are about to permanently delete <strong style={{ color: '#dc2626' }}>{company.name}</strong>.
          </p>
          <p style={{ marginBottom: '12px' }}>
            This action is <strong style={{ color: '#dc2626' }}>IRREVERSIBLE</strong> and will permanently delete:
          </p>
          <ul style={{ paddingLeft: '24px', marginBottom: '12px', color: '#374151' }}>
            <li>Company profile and all settings</li>
            <li>Company administrator account</li>
            <li>All staff and manager accounts ({company.currentUsers} users)</li>
            <li>All job assignments</li>
            <li>All time entries and attendance records</li>
            <li>All messages</li>
          </ul>
          <div
            style={{
              padding: '16px',
              backgroundColor: '#fef2f2',
              border: '2px solid #dc2626',
              borderRadius: '8px',
              marginTop: '16px',
            }}
          >
            <p style={{ color: '#dc2626', fontWeight: '700', fontSize: '16px', margin: 0 }}>
              THIS CANNOT BE UNDONE!
            </p>
          </div>
        </div>

        <ModalButtonGroup
          onCancel={onClose}
          onSubmit={handleFirstConfirmation}
          submitLabel="I Understand, Continue"
          submitVariant="danger"
        />
      </BaseModal>
    );
  }

  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      title="Final Confirmation"
      size="medium"
      closeOnEscape={!loading}
      closeOnBackdropClick={!loading}
      loading={loading}
    >
      <div style={{ marginBottom: '24px', color: '#6b7280', fontSize: '15px', lineHeight: '1.6' }}>
        <p style={{ marginBottom: '16px', color: '#111827' }}>
          To confirm permanent deletion, please type the company name exactly as shown below:
        </p>
        <div
          style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            marginBottom: '16px',
            fontFamily: 'monospace',
            fontSize: '16px',
            fontWeight: '600',
            color: '#111827',
          }}
        >
          {company.name}
        </div>
        <input
          type="text"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          placeholder="Type company name here"
          autoFocus
          style={{
            ...INPUT_STYLE,
            border: '2px solid #dc2626',
          }}
        />
      </div>

      {error && (
        <div style={ERROR_MESSAGE_STYLE}>
          {error}
        </div>
      )}

      <ModalButtonGroup
        onCancel={() => setStep('first')}
        onSubmit={handleFinalDelete}
        submitLabel="Permanently Delete Company"
        cancelLabel="Back"
        submitVariant="danger"
        isLoading={loading}
        loadingLabel="Deleting..."
        disabled={confirmationText !== company.name}
      />
    </BaseModal>
  );
}
